// src/components/EnergyComparisonWidget.js - Updated with language support
import React, { useState, useEffect } from 'react';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  Legend, ResponsiveContainer, Area
} from 'recharts';
import '../css/EnergyComparisonWidget.css';

const EnergyComparisonWidget = ({ language = 'nl' }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [totalComparison, setTotalComparison] = useState(null);
  const [hasTwoYearsAgoData, setHasTwoYearsAgoData] = useState(false);
  const [hasThreeYearsAgoData, setHasThreeYearsAgoData] = useState(false);
  const [hasFourYearsAgoData, setHasFourYearsAgoData] = useState(false);
  const [baselineComparison, setBaselineComparison] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Get current energy year for reference
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();
  const currentEnergyYear = currentMonth >= 7 ? 
    `${currentYear}-${currentYear+1}` : 
    `${currentYear-1}-${currentYear}`;
  const previousEnergyYear = `${parseInt(currentEnergyYear.split('-')[0]) - 1}-${parseInt(currentEnergyYear.split('-')[1]) - 1}`;
  const twoYearsAgoEnergyYear = `${parseInt(previousEnergyYear.split('-')[0]) - 1}-${parseInt(previousEnergyYear.split('-')[1]) - 1}`;
  const threeYearsAgoEnergyYear = `${parseInt(twoYearsAgoEnergyYear.split('-')[0]) - 1}-${parseInt(twoYearsAgoEnergyYear.split('-')[1]) - 1}`;
  const fourYearsAgoEnergyYear = `${parseInt(threeYearsAgoEnergyYear.split('-')[0]) - 1}-${parseInt(threeYearsAgoEnergyYear.split('-')[1]) - 1}`;

  // Translations
  const translations = {
    nl: {
      title: "📊 Gasverbruik VVE Polderzicht",
      lastUpdate: "Laatste update",
      loading: "Gegevens laden...",
      error: "Kon de energiegegevens niet laden. Probeer het later nog eens.",
      currentYear: `Dit jaar (${currentEnergyYear})`,
      previousYear: `Vorig jaar (${previousEnergyYear})`,
      twoYearsAgo: `${twoYearsAgoEnergyYear}`,
      threeYearsAgo: `${threeYearsAgoEnergyYear}`,
      fourYearsAgo: `${fourYearsAgoEnergyYear}`,
      prediction: "Voorspelling",
      tooltipCurrentYear: "Dit jaar (totaal)",
      tooltipPreviousYear: "Vorig jaar (totaal)",
      tooltipTwoYearsAgo: `${twoYearsAgoEnergyYear} (totaal)`,
      tooltipThreeYearsAgo: `${threeYearsAgoEnergyYear} (totaal)`,
      tooltipFourYearsAgo: `${fourYearsAgoEnergyYear} (totaal)`,
      tooltipPrediction: "Voorspelling",
      totalCurrentYear: "Totaal dit jaar",
      totalPrediction: "Voorspelling dit jaar",
      totalPreviousYear: "Totaal vorig jaar",
      upTo: "t/m",
      change: "Verandering",
      saving: "Besparing",
      increase: "Stijging",
      savingsSince: (year, percent) => `🌱 Sinds energiejaar ${year} is het gasverbruik met ${percent}% gedaald (op basis van voorspelling dit jaar).`,
      increaseSince: (year, percent) => `⚠️ Sinds energiejaar ${year} is het gasverbruik met ${percent}% gestegen (op basis van voorspelling dit jaar).`,
      infoText: "De energieverbruiksgegevens worden bijgewerkt op maandelijkse basis. De voorspelling is gebaseerd op het seizoenspatroon van ons gebouw."
    },
    en: {
      title: "📊 Gas usage VVE Polderzicht",
      lastUpdate: "Last update",
      loading: "Loading data...",
      error: "Could not load energy data. Please try again later.",
      currentYear: `This year (${currentEnergyYear})`,
      previousYear: `Last year (${previousEnergyYear})`,
      twoYearsAgo: `${twoYearsAgoEnergyYear}`,
      threeYearsAgo: `${threeYearsAgoEnergyYear}`,
      fourYearsAgo: `${fourYearsAgoEnergyYear}`,
      prediction: "Prediction",
      tooltipCurrentYear: "This year (total)",
      tooltipPreviousYear: "Last year (total)",
      tooltipTwoYearsAgo: `${twoYearsAgoEnergyYear} (total)`,
      tooltipThreeYearsAgo: `${threeYearsAgoEnergyYear} (total)`,
      tooltipFourYearsAgo: `${fourYearsAgoEnergyYear} (total)`,
      tooltipPrediction: "Prediction",
      totalCurrentYear: "Total this year",
      totalPrediction: "Prediction this year",
      totalPreviousYear: "Total last year",
      upTo: "until",
      change: "Change",
      saving: "Reduction",
      increase: "Increase",
      savingsSince: (year, percent) => `🌱 Since energy year ${year}, gas usage has decreased by ${percent}% (based on this year's forecast).`,
      increaseSince: (year, percent) => `⚠️ Since energy year ${year}, gas usage has increased by ${percent}% (based on this year's forecast).`,
      infoText: "The energy usage data is updated on a monthly basis. The prediction is based on the seasonal pattern of our building."
    }
  };
  
  // Get current text based on language
  const t = translations[language] || translations.nl;
  
  // Listen for window resize events
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch public energy usage data from your API
      const response = await fetch('/api/public/energy-usage');
      
      if (!response.ok) {
        throw new Error('Failed to fetch energy data');
      }
      
      const data = await response.json();
      
      // Process the data for the chart
      processData(data);
      
      // Set last update timestamp if available
      if (data.lastUpdate) {
        setLastUpdate(new Date(data.lastUpdate).toLocaleString(language === 'en' ? 'en-US' : 'nl-NL'));
      }
      
    } catch (err) {
      console.error('Error fetching energy data:', err);
      setError(t.error);
    } finally {
      setLoading(false);
    }
  };
  
  const processData = (data) => {
    // Process monthly data for current and previous years
    const currentYearData = data[currentEnergyYear]?.monthly || {};
    const previousYearData = data[previousEnergyYear]?.monthly || {};
    const twoYearsAgoData = data[twoYearsAgoEnergyYear]?.monthly || {};
    const threeYearsAgoData = data[threeYearsAgoEnergyYear]?.monthly || {};
    const fourYearsAgoData = data[fourYearsAgoEnergyYear]?.monthly || {};
    
    // Define month order for energy year (July to June)
    const monthOrder = [
      '07', '08', '09', '10', '11', '12', 
      '01', '02', '03', '04', '05', '06'
    ];
    
    // Map for converting month numbers to names
    const monthNames = language === 'en' ? 
      {
        '01': 'Jan', '02': 'Feb', '03': 'Mar', '04': 'Apr',
        '05': 'May', '06': 'Jun', '07': 'Jul', '08': 'Aug',
        '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dec'
      } : 
      {
        '01': 'Jan', '02': 'Feb', '03': 'Mrt', '04': 'Apr',
        '05': 'Mei', '06': 'Jun', '07': 'Jul', '08': 'Aug',
        '09': 'Sep', '10': 'Okt', '11': 'Nov', '12': 'Dec'
      };
    
    // Seasonality map for prediction calculations
    const seasonalityMap = {
      '07': 0.01, '08': 0.03, '09': 0.05, '10': 0.12,
      '11': 0.24, '12': 0.40, '01': 0.57, '02': 0.71,
      '03': 0.84, '04': 0.93, '05': 0.98, '06': 1.00
    };
    
    // Calculate the latest month with data
    let latestMonth = null;
    
    for (const month in currentYearData) {
      if (currentYearData[month] > 0) {
        // Get the month part (MM) from YYYY-MM
        const monthPart = month.split('-')[1];
        if (!latestMonth || monthOrder.indexOf(monthPart) > monthOrder.indexOf(latestMonth)) {
          latestMonth = monthPart;
        }
      }
    }
    
    // Calculate current year total
    const currentTotal = Object.values(currentYearData).reduce((sum, val) => sum + (val || 0), 0);
    
    // Calculate previous year total
    const previousTotal = Object.values(previousYearData).reduce((sum, val) => sum + (val || 0), 0);
    
    // Calculate predicted total using the seasonality data
    const seasonFraction = latestMonth ? seasonalityMap[latestMonth] || 0 : 0;
    const predictedTotal = seasonFraction > 0 ? Math.round(currentTotal / seasonFraction) : currentTotal;
    
    // Calculate percent change
    const percentChange = calculatePercentChange(predictedTotal, previousTotal);
    
    // Prepare chart data showing cumulative usage
    let currentYearCumulative = 0;
    let previousYearCumulative = 0;
    let twoYearsAgoCumulative = 0;
    let threeYearsAgoCumulative = 0;
    let fourYearsAgoCumulative = 0;
    let predictedCumulative = 0;

    // Track whether each historical year actually has any data
    const twoYearsAgoHasData = Object.values(twoYearsAgoData).some(v => v > 0);
    const threeYearsAgoHasData = Object.values(threeYearsAgoData).some(v => v > 0);
    const fourYearsAgoHasData = Object.values(fourYearsAgoData).some(v => v > 0);
    setHasTwoYearsAgoData(twoYearsAgoHasData);
    setHasThreeYearsAgoData(threeYearsAgoHasData);
    setHasFourYearsAgoData(fourYearsAgoHasData);

    const chartDataPoints = monthOrder.map(monthNum => {
      // Extract year parts from energy year strings
      const currentEnergyYearParts = currentEnergyYear.split('-');
      const previousEnergyYearParts = previousEnergyYear.split('-');
      const twoYearsAgoParts = twoYearsAgoEnergyYear.split('-');
      const threeYearsAgoParts = threeYearsAgoEnergyYear.split('-');
      const fourYearsAgoParts = fourYearsAgoEnergyYear.split('-');

      const currentStartYear = parseInt(currentEnergyYearParts[0]);
      const currentEndYear = parseInt(currentEnergyYearParts[1]);
      const previousStartYear = parseInt(previousEnergyYearParts[0]);
      const previousEndYear = parseInt(previousEnergyYearParts[1]);
      const twoYearsAgoStartYear = parseInt(twoYearsAgoParts[0]);
      const twoYearsAgoEndYear = parseInt(twoYearsAgoParts[1]);
      const threeYearsAgoStartYear = parseInt(threeYearsAgoParts[0]);
      const threeYearsAgoEndYear = parseInt(threeYearsAgoParts[1]);
      const fourYearsAgoStartYear = parseInt(fourYearsAgoParts[0]);
      const fourYearsAgoEndYear = parseInt(fourYearsAgoParts[1]);

      // Determine correct year for the month
      const currentYear = monthNum >= '07' ? currentStartYear : currentEndYear;
      const previousYear = monthNum >= '07' ? previousStartYear : previousEndYear;
      const twoYearsAgoYear = monthNum >= '07' ? twoYearsAgoStartYear : twoYearsAgoEndYear;
      const threeYearsAgoYear = monthNum >= '07' ? threeYearsAgoStartYear : threeYearsAgoEndYear;
      const fourYearsAgoYear = monthNum >= '07' ? fourYearsAgoStartYear : fourYearsAgoEndYear;

      // Construct month strings
      const currentMonth = `${currentYear}-${monthNum}`;
      const previousMonth = `${previousYear}-${monthNum}`;
      const twoYearsAgoMonth = `${twoYearsAgoYear}-${monthNum}`;
      const threeYearsAgoMonth = `${threeYearsAgoYear}-${monthNum}`;
      const fourYearsAgoMonth = `${fourYearsAgoYear}-${monthNum}`;

      // Get usage data
      const currentUsage = currentYearData[currentMonth] || 0;
      const previousUsage = previousYearData[previousMonth] || 0;
      const twoYearsAgoUsage = twoYearsAgoData[twoYearsAgoMonth] || 0;
      const threeYearsAgoUsage = threeYearsAgoData[threeYearsAgoMonth] || 0;
      const fourYearsAgoUsage = fourYearsAgoData[fourYearsAgoMonth] || 0;

      // Add to cumulative totals
      currentYearCumulative += currentUsage;
      previousYearCumulative += previousUsage;
      twoYearsAgoCumulative += twoYearsAgoUsage;
      threeYearsAgoCumulative += threeYearsAgoUsage;
      fourYearsAgoCumulative += fourYearsAgoUsage;
      
      // Calculate predicted value for this month
      if (currentUsage > 0) {
        predictedCumulative = currentYearCumulative;
      } 
      // For future months, use the predicted monthly values
      else if (latestMonth) {
        // Get the month's position in the energy year
        const monthIndex = monthOrder.indexOf(monthNum);
        const latestMonthIndex = monthOrder.indexOf(latestMonth);
        
        // Only predict for months after the latest month with data
        if (monthIndex > latestMonthIndex) {
          // Get seasonality fractions
          const thisMonthFraction = seasonalityMap[monthNum];
          
          // Calculate the expected total by this month
          const expectedTotal = predictedTotal * thisMonthFraction;
          
          // Set the cumulative prediction
          predictedCumulative = expectedTotal;
        }
      }
      
      // Format month label based on screen size
      const monthLabel = windowWidth < 600 ? 
        monthNames[monthNum].charAt(0) : // First letter only for small screens
        monthNames[monthNum];
      
      return {
        month: monthLabel,
        currentYearCumulative: Math.round(currentYearCumulative),
        previousYearCumulative: Math.round(previousYearCumulative),
        twoYearsAgoCumulative: twoYearsAgoHasData ? Math.round(twoYearsAgoCumulative) : null,
        threeYearsAgoCumulative: threeYearsAgoHasData ? Math.round(threeYearsAgoCumulative) : null,
        fourYearsAgoCumulative: fourYearsAgoHasData ? Math.round(fourYearsAgoCumulative) : null,
        predictedCumulative: predictedCumulative > 0 ? Math.round(predictedCumulative) : null,
        // Keep original monthly values for the tooltip
        currentYear: currentUsage,
        previousYear: previousUsage,
        twoYearsAgo: twoYearsAgoUsage,
        threeYearsAgo: threeYearsAgoUsage,
        fourYearsAgo: fourYearsAgoUsage,
      };
    });

    // Set chart data
    setChartData(chartDataPoints);
    
    // Set comparison data for the summary section
    setTotalComparison({
      current: currentTotal,
      previous: previousTotal,
      predicted: predictedTotal,
      percentChange,
      latestMonth: latestMonth ? monthNames[latestMonth] : null
    });

    // Calculate savings vs the fixed 2022-2023 baseline year
    const BASELINE_YEAR = '2022-2023';
    const baselineData = data[BASELINE_YEAR]?.monthly || {};
    const baselineTotal = Object.values(baselineData).reduce((sum, val) => sum + (val || 0), 0);
    if (baselineTotal > 0) {
      const percent = ((predictedTotal - baselineTotal) / baselineTotal) * 100;
      setBaselineComparison({
        year: BASELINE_YEAR,
        baselineTotal,
        predictedTotal,
        percent
      });
    } else {
      setBaselineComparison(null);
    }
  };
  
  // Calculate percentage change between values
  const calculatePercentChange = (current, previous) => {
    if (!previous || previous === 0) return '∞';
    const change = ((current - previous) / previous) * 100;
    return change.toFixed(1);
  };
  
  const formatNumber = (num) => {
    if (num === undefined || num === null) return 'N/A';
    return new Intl.NumberFormat(language === 'en' ? 'en-US' : 'nl-NL').format(Math.round(num));
  };
  
  const renderTooltip = (props) => {
    if (!props.active || !props.payload || props.payload.length === 0) {
      return null;
    }

    const data = props.payload[0].payload;
    
    return (
      <div className="custom-tooltip">
        <p className="tooltip-label">{data.month}</p>
        <p className="tooltip-value">
          <span className="tooltip-label-inner">{t.tooltipCurrentYear}:</span> {formatNumber(data.currentYearCumulative)} m³
        </p>
        <p className="tooltip-value">
          <span className="tooltip-label-inner">{t.tooltipPreviousYear}:</span> {formatNumber(data.previousYearCumulative)} m³
        </p>
        {data.twoYearsAgoCumulative != null && (
          <p className="tooltip-value">
            <span className="tooltip-label-inner">{t.tooltipTwoYearsAgo}:</span> {formatNumber(data.twoYearsAgoCumulative)} m³
          </p>
        )}
        {data.threeYearsAgoCumulative != null && (
          <p className="tooltip-value">
            <span className="tooltip-label-inner">{t.tooltipThreeYearsAgo}:</span> {formatNumber(data.threeYearsAgoCumulative)} m³
          </p>
        )}
        {data.fourYearsAgoCumulative != null && (
          <p className="tooltip-value">
            <span className="tooltip-label-inner">{t.tooltipFourYearsAgo}:</span> {formatNumber(data.fourYearsAgoCumulative)} m³
          </p>
        )}
        {data.predictedCumulative && data.currentYearCumulative !== data.predictedCumulative && (
          <p className="tooltip-value">
            <span className="tooltip-label-inner">{t.tooltipPrediction}:</span> {formatNumber(data.predictedCumulative)} m³
          </p>
        )}
      </div>
    );
  };
  
  // Determine chart height based on screen width
  const getChartHeight = () => {
    if (windowWidth < 400) return 180;
    if (windowWidth < 600) return 220;
    return 250;
  };
  
  // Determine if we should hide the legend based on screen width
  const shouldShowLegend = windowWidth >= 500;
  
  // Determine if we should use a horizontal layout for totals based on screen width
  const useHorizontalLayout = windowWidth >= 768;
  
  if (loading) {
    return (
      <div className="energy-widget2">
        <h3>{t.title}</h3>
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>{t.loading}</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="energy-widget2">
        <h3>{t.title}</h3>
        <div className="error-message">
          <p>{error}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="energy-widget2">
      <h3>{t.title}</h3>
      
      {lastUpdate && (
        <div className="last-update">
          {t.lastUpdate}: {lastUpdate}
        </div>
      )}
      
      <div className="chart-container3">
        <ResponsiveContainer width="100%" height={getChartHeight()}>
          <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="month" 
              tick={{ fontSize: windowWidth < 500 ? 10 : 12 }}
              tickMargin={8}
            />
            <YAxis 
              tick={{ fontSize: windowWidth < 500 ? 10 : 12 }}
              width={30}
            />
            <Tooltip content={renderTooltip} />
            {shouldShowLegend && <Legend wrapperStyle={{ fontSize: windowWidth < 500 ? 10 : 12 }} />}
            
            {/* Area chart for current year cumulative usage */}
            <Area
              type="monotone"
              dataKey="currentYearCumulative"
              name={t.currentYear}
              fill="#FBBF24"
              fillOpacity={0.3}
              stroke="#FBBF24"
              strokeWidth={2}
              activeDot={{ r: windowWidth < 500 ? 4 : 6 }}
            />
            
            {/* Line chart for previous year cumulative usage */}
            <Line
              type="monotone"
              dataKey="previousYearCumulative"
              name={t.previousYear}
              stroke="#4E8DF7"
              strokeWidth={2}
              dot={{ r: windowWidth < 500 ? 3 : 4 }}
              activeDot={{ r: windowWidth < 500 ? 5 : 6 }}
            />

            {/* Line chart for two-years-ago cumulative usage */}
            {hasTwoYearsAgoData && (
              <Line
                type="monotone"
                dataKey="twoYearsAgoCumulative"
                name={t.twoYearsAgo}
                stroke="#10B981"
                strokeWidth={1.5}
                dot={{ r: windowWidth < 500 ? 2 : 3 }}
                activeDot={{ r: windowWidth < 500 ? 4 : 5 }}
                connectNulls
              />
            )}

            {/* Line chart for three-years-ago cumulative usage */}
            {hasThreeYearsAgoData && (
              <Line
                type="monotone"
                dataKey="threeYearsAgoCumulative"
                name={t.threeYearsAgo}
                stroke="#A855F7"
                strokeWidth={1.5}
                dot={{ r: windowWidth < 500 ? 2 : 3 }}
                activeDot={{ r: windowWidth < 500 ? 4 : 5 }}
                connectNulls
              />
            )}

            {/* Line chart for four-years-ago cumulative usage */}
            {hasFourYearsAgoData && (
              <Line
                type="monotone"
                dataKey="fourYearsAgoCumulative"
                name={t.fourYearsAgo}
                stroke="#EC4899"
                strokeWidth={1.5}
                dot={{ r: windowWidth < 500 ? 2 : 3 }}
                activeDot={{ r: windowWidth < 500 ? 4 : 5 }}
                connectNulls
              />
            )}

            {/* Dashed line for prediction */}
            <Line
              type="monotone"
              dataKey="predictedCumulative"
              name={t.prediction}
              stroke="#000000"
              strokeWidth={2}
              strokeDasharray="4 4"
              dot={false}
              activeDot={{ r: windowWidth < 500 ? 4 : 5 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      
      {!shouldShowLegend && (
        <div className="compact-legend">
          <span className="legend-item"><span className="color-box current"></span> {t.currentYear.split(' ')[0]}</span>
          <span className="legend-item"><span className="color-box previous"></span> {t.previousYear.split(' ')[0]}</span>
          {hasTwoYearsAgoData && (
            <span className="legend-item"><span className="color-box two-years-ago"></span> {t.twoYearsAgo}</span>
          )}
          {hasThreeYearsAgoData && (
            <span className="legend-item"><span className="color-box three-years-ago"></span> {t.threeYearsAgo}</span>
          )}
          {hasFourYearsAgoData && (
            <span className="legend-item"><span className="color-box four-years-ago"></span> {t.fourYearsAgo}</span>
          )}
          <span className="legend-item"><span className="color-box prediction"></span> {t.prediction}</span>
        </div>
      )}
      
      {totalComparison && (
        <div className={`total-comparison ${useHorizontalLayout ? 'horizontal' : 'vertical'}`}>
          <div className="total-metric">
            <h4>{t.totalCurrentYear}</h4>
            <span className="metric-value">{formatNumber(totalComparison.current)} m³</span>
            {totalComparison.latestMonth && (
              <div className="metric-note">{t.upTo} {totalComparison.latestMonth}</div>
            )}
          </div>
          
          <div className="total-metric">
            <h4>{t.totalPrediction}</h4>
            <span className="metric-value prediction">{formatNumber(totalComparison.predicted)} m³</span>
          </div>
          
          <div className="total-metric">
            <h4>{t.totalPreviousYear}</h4>
            <span className="metric-value">{formatNumber(totalComparison.previous)} m³</span>
          </div>
          
          <div className="total-metric">
            <h4>{t.change}</h4>
            <span className={`metric-value ${parseFloat(totalComparison.percentChange) < 0 ? 'negative-change' : 'positive-change'}`}>
              {totalComparison.percentChange}%
            </span>
            <div className="metric-note">
              {parseFloat(totalComparison.percentChange) < 0 ? t.saving : t.increase}
            </div>
          </div>
        </div>
      )}
      
      {baselineComparison && (
        <div className={`baseline-savings ${baselineComparison.percent < 0 ? 'savings' : 'increase'}`}>
          {baselineComparison.percent < 0
            ? t.savingsSince(baselineComparison.year, Math.abs(baselineComparison.percent).toFixed(1))
            : t.increaseSince(baselineComparison.year, baselineComparison.percent.toFixed(1))}
        </div>
      )}

      <div className="energy-info">
        <p>{t.infoText}</p>
      </div>
    </div>
  );
};

export default EnergyComparisonWidget;